import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Flex, Badge, Button, List, ListItem, ListIcon } from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { FaSolarPanel, FaHome, FaCheckCircle, FaLeaf, FaDollarSign, FaBolt } from "react-icons/fa";

const NAVY = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const FinanceServicesPropertySolarFinance: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/property-solar-finance"
        fallbackTitle="Property + Solar Finance – Combined Package"
        fallbackDescription="Combined property and solar financing solutions. Bundle your home loan with solar installation for enhanced savings and sustainability."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
              Finance Section
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Property + Solar Finance
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Combined property and solar financing for maximum savings and sustainability</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={16}>
          {/* Package Overview */}
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full" mb={4}>
              Combined Package Savings
            </Badge>
            <Heading size="xl" mb={4}>Property + Solar Finance Package</Heading>
            <Text fontSize="lg" color="gray.600">
              Strengthen your investment position with our integrated property and solar financing solution.
              Bundle your home loan with solar installation for enhanced savings and sustainability.
            </Text>
          </Box>

          {/* Two-Column Features */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} w="full">
            {/* Property Finance Column */}
            <Box bg="gray.50" p={8} borderRadius="xl" border="2px solid" borderColor="gray.200">
              <Flex align="center" mb={4}>
                <Icon as={FaHome} boxSize={8} color={NAVY} mr={3} />
                <Heading size="lg" color={NAVY}>Property Finance</Heading>
              </Flex>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Competitive home loan rates from 5.99%
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Up to 95% LVR available
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Fixed and variable options
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Offset account included
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Redraw facility available
                </ListItem>
              </List>
            </Box>

            {/* Solar Finance Column */}
            <Box bg={SAND_LIGHT} p={8} borderRadius="xl" border="2px solid" borderColor={SAND}>
              <Flex align="center" mb={4}>
                <Icon as={FaSolarPanel} boxSize={8} color={NAVY} mr={3} />
                <Heading size="lg" color={NAVY}>Solar Finance</Heading>
              </Flex>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Finance up to $30,000 for solar systems
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Interest rates from 6.49%
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Terms up to 10 years
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Government rebates assistance
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  Installation coordination included
                </ListItem>
              </List>
            </Box>
          </SimpleGrid>

          {/* Package Benefits */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={8}>Combined Package Benefits</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="md">
                <Icon as={FaDollarSign} boxSize={10} color={SAND} mb={3} />
                <Heading size="md" mb={2}>Reduced Fees</Heading>
                <Text color="gray.600">Save on application and establishment fees when bundling property and solar finance together.</Text>
              </Box>
              <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="md">
                <Icon as={FaLeaf} boxSize={10} color={SAND} mb={3} />
                <Heading size="md" mb={2}>Eco-Friendly</Heading>
                <Text color="gray.600">Reduce your carbon footprint while increasing property value with sustainable energy solutions.</Text>
              </Box>
              <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="md">
                <Icon as={FaBolt} boxSize={10} color={SAND} mb={3} />
                <Heading size="md" mb={2}>Energy Savings</Heading>
                <Text color="gray.600">Lower electricity bills offset loan repayments, making your property more affordable long-term.</Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* CTA Section */}
          <Box bg={NAVY} p={10} borderRadius="xl" textAlign="center" w="full">
            <Heading size="lg" color="white" mb={4}>Ready to Go Solar?</Heading>
            <Text color="rgba(255,255,255,0.9)" fontSize="lg" mb={6} maxW="2xl" mx="auto">
              Combine your property purchase or refinance with solar installation for maximum benefits.
              Our finance specialists will help structure the perfect package for your needs.
            </Text>
            <Flex justify="center" gap={4} flexWrap="wrap">
              <Button size="lg" bg={SAND} color={NAVY} _hover={{ opacity: 0.9 }}>
                Get a Package Quote
              </Button>
              <Button size="lg" variant="outline" borderColor="white" color="white" _hover={{ bg: "whiteAlpha.200" }}>
                Download Package Guide
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesPropertySolarFinance;
